/**
 * AIDiagnosisModal — snap/upload a leaf or tree photo + describe a concern, get a
 * structured AI assessment (feature #3): likely problems, suggested tasks, and
 * potential yield-impact events. Review-before-save: this surfaces suggestions;
 * the farmer decides what to act on. The image is downscaled on a <canvas> before
 * upload so it fits under the API's 2MB body limit. Backed by POST /ai/diagnose.
 */
import { useState } from 'react'
import { apiError } from '../../lib/errors'
import { useMutation } from '@tanstack/react-query'
import { X, Camera, AlertTriangle, CheckSquare, TrendingDown, Sparkles, Plus, Check } from 'lucide-react'
import { diagnosisApi, tasksApi, yieldImpactApi, type DiagnosisResult } from '../../lib/api'

// Map AI severities/types to the yield-impact + task enums.
const SEV: Record<string, string> = { minor: 'low', moderate: 'medium', severe: 'high', low: 'low', medium: 'medium', high: 'high', critical: 'critical' }
const YE_TYPE: Record<string, string> = { pest: 'pest', disease: 'disease', weather: 'weather', nutrient: 'other', other: 'other' }

const C = {
  card: 'var(--color-bg-card)', bg: 'var(--color-bg)', border: 'rgba(255,255,255,0.08)',
  amber: 'var(--color-primary)', green: '#2D6A2F', blue: '#5B8DEF', red: '#E5533C',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}

// downscaleImage renders the file to a canvas (max edge 1280px) and returns a
// compressed JPEG Blob — keeps phone photos comfortably under the 2MB limit.
async function downscaleImage(file: File, maxEdge = 1280, quality = 0.82): Promise<Blob> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader()
    r.onload = () => res(r.result as string)
    r.onerror = rej
    r.readAsDataURL(file)
  })
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image()
    i.onload = () => res(i)
    i.onerror = rej
    i.src = dataUrl
  })
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
  return await new Promise<Blob>((res, rej) =>
    canvas.toBlob(b => b ? res(b) : rej(new Error('encode failed')), 'image/jpeg', quality))
}

export function AIDiagnosisModal({ plantLabel, plantId, onClose }: { plantLabel?: string; plantId?: string; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [concern, setConcern] = useState('')
  const [result, setResult] = useState<DiagnosisResult | null>(null)
  const [added, setAdded] = useState<Record<string, boolean>>({})

  const today = new Date().toISOString().slice(0, 10)
  const addTask = useMutation({
    mutationFn: (v: { key: string; title: string; detail: string; priority: string }) =>
      tasksApi.create({
        title: v.title, description: v.detail, type: 'inspection',
        priority: ['urgent', 'high', 'normal', 'low'].includes(v.priority) ? v.priority : 'normal',
        resource_type: plantId ? 'plant' : '', resource_id: plantId || undefined, resource_label: plantLabel || '',
      }),
    onSuccess: (_d, v) => setAdded(a => ({ ...a, [v.key]: true })),
  })
  const addEvent = useMutation({
    mutationFn: (v: { key: string; type: string; severity: string; note: string }) =>
      yieldImpactApi.create({
        event_type: YE_TYPE[v.type] ?? 'other', severity: SEV[v.severity] ?? 'medium',
        title: `${v.type} (auto-detected)`, description: v.note, occurred_at: today,
        affected_plant_ids: plantId ? [plantId] : [], source: 'ai',
      }),
    onSuccess: (_d, v) => setAdded(a => ({ ...a, [v.key]: true })),
  })

  const run = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a photo first')
      const blob = await downscaleImage(file)
      const form = new FormData()
      form.append('image', blob, 'photo.jpg')
      form.append('concern', concern)
      if (plantLabel) form.append('plant_label', plantLabel)
      return diagnosisApi.diagnose(form).then(r => r.data)
    },
    onSuccess: setResult,
  })

  function pick(f: File | null) {
    setFile(f); setResult(null)
    setPreview(p => { if (p) URL.revokeObjectURL(p); return f ? URL.createObjectURL(f) : null })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 520, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={16} style={{ color: C.amber }} /> Automatic photo diagnosis{plantLabel ? ` — ${plantLabel}` : ''}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 140, border: `1.5px dashed ${C.border}`, borderRadius: 10, cursor: 'pointer', color: C.muted, background: C.bg, overflow: 'hidden' }}>
            {preview
              ? <img src={preview} alt="selected" style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain' }} />
              : <><Camera size={28} /><span style={{ fontSize: 13 }}>Tap to take or choose a photo</span></>}
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => pick(e.target.files?.[0] ?? null)} />
          </label>

          <div>
            <label style={{ fontSize: 12, color: C.muted }}>What are you concerned about?</label>
            <textarea value={concern} onChange={e => setConcern(e.target.value)} placeholder="e.g. yellow spots on leaves, possible scale insects…"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 4, minHeight: 64, resize: 'vertical', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          </div>

          <button disabled={!file || run.isPending} onClick={() => run.mutate()}
            style={{ padding: '10px 0', borderRadius: 8, background: C.amber, border: 'none', color: '#1a1300', fontSize: 14, fontWeight: 700, cursor: file && !run.isPending ? 'pointer' : 'not-allowed' }}>
            {run.isPending ? 'Analyzing…' : 'Diagnose'}
          </button>
          {run.isError && <div style={{ fontSize: 12, color: C.red }}>{apiError(run.error, 'Failed')}</div>}

          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              {result.warning && <div style={{ fontSize: 12, color: C.amber, background: 'color-mix(in srgb, var(--color-primary) 10%, transparent)', padding: '8px 10px', borderRadius: 8 }}>{result.warning}</div>}
              {result.summary && <p style={{ margin: 0, fontSize: 14, color: C.text, lineHeight: 1.5 }}>{result.summary}</p>}

              {result.problems.length > 0 && (
                <Section icon={<AlertTriangle size={14} style={{ color: C.amber }} />} title="Likely problems">
                  {result.problems.map((p, i) => (
                    <div key={i} style={row}>
                      <div style={{ fontWeight: 600, color: C.text }}>{p.name} <Pill text={p.likelihood} /></div>
                      {p.detail && <div style={{ fontSize: 12, color: C.muted }}>{p.detail}</div>}
                    </div>
                  ))}
                </Section>
              )}

              {result.suggested_tasks.length > 0 && (
                <Section icon={<CheckSquare size={14} style={{ color: C.green }} />} title="Suggested tasks">
                  {result.suggested_tasks.map((t, i) => (
                    <div key={i} style={row}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontWeight: 600, color: C.text }}>{t.title} <Pill text={t.priority} /></div>
                        <AddBtn done={!!added[`task-${i}`]} busy={addTask.isPending}
                          onClick={() => addTask.mutate({ key: `task-${i}`, title: t.title, detail: t.detail, priority: t.priority })} label="task" />
                      </div>
                      {t.detail && <div style={{ fontSize: 12, color: C.muted }}>{t.detail}</div>}
                    </div>
                  ))}
                </Section>
              )}

              {result.suggested_yield_events.length > 0 && (
                <Section icon={<TrendingDown size={14} style={{ color: C.red }} />} title="Potential yield impact">
                  {result.suggested_yield_events.map((y, i) => (
                    <div key={i} style={row}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontWeight: 600, color: C.text }}>{y.type} <Pill text={y.severity} /></div>
                        <AddBtn done={!!added[`ye-${i}`]} busy={addEvent.isPending}
                          onClick={() => addEvent.mutate({ key: `ye-${i}`, type: y.type, severity: y.severity, note: y.note })} label="event" />
                      </div>
                      {y.note && <div style={{ fontSize: 12, color: C.muted }}>{y.note}</div>}
                    </div>
                  ))}
                </Section>
              )}

              <div style={{ fontSize: 11, color: C.faint }}>
                Automatic suggestions for review — not a substitute for an agronomist. Create the tasks/events you want to keep from the Tasks and Yield Impact pages.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>{icon} {title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  )
}
function AddBtn({ done, busy, onClick, label }: { done: boolean; busy: boolean; onClick: () => void; label: string }) {
  if (done) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4ADE80', whiteSpace: 'nowrap' }}><Check size={12} /> added</span>
  return (
    <button onClick={onClick} disabled={busy} title={`Add ${label}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 7, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.amber, cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
      <Plus size={12} /> {label}
    </button>
  )
}
function Pill({ text }: { text: string }) {
  return <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: 'rgba(255,255,255,0.08)', color: C.muted, marginLeft: 6, textTransform: 'uppercase' }}>{text}</span>
}
const row: React.CSSProperties = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 13 }
